<template>
  <div
    ref="wrapperRef"
    class="color-picker-wrapper"
    @focusout="onPickerFocusOut"
    @keydown.escape.stop="onEscape"
  >
    <div class="color-input w-full">
      <label v-if="inputLabel" :id="inputLabelId" :for="inputId" class="mt-2">
        {{ inputLabel }}
      </label>
      <InputField
        @click="toggleModal"
        @keydown.enter.prevent="toggleModal"
        @keydown.space.prevent="toggleModal"
        @keydown.esc.prevent="closeModal"
        readonly
        class="color-palette"
        :id="inputId"
        :label-id="inputLabelId"
        :input-aria-label="triggerInputAriaLabel"
        :input-aria-expanded="state.openModal ? 'true' : 'false'"
        input-aria-haspopup="dialog"
        :style="'background-color: ' + selectedColorHex"
        :invalid-feedback="invalidFeedback"
      ></InputField>
    </div>
    <div
      v-show="state.openModal"
      class="color-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('colorPicker.dialogAriaLabel') || 'Color picker dialog'"
    >
      <div class="colors" role="radiogroup" :aria-label="radiogroupAriaLabel">
        <button
          v-for="color in colorPalette"
          :key="color.hex"
          :ref="(element) => setColorButtonRef(element, color.hex)"
          type="button"
          class="color-item"
          :class="{ active: selectedColorHex == color.hex }"
          :style="'background-color:' + color.hex"
          role="radio"
          :aria-checked="isColorSelected(color.hex) ? 'true' : 'false'"
          :tabindex="getRadioTabindex(color.hex)"
          :aria-label="getColorAriaLabel(color)"
          @keydown="onColorRadioKeydown($event, color.hex)"
          @click="setColor(color.hex)"
        >
          <span
            v-if="isColorSelected(color.hex)"
            class="color-item-check"
            aria-hidden="true"
            >✓</span
          >
          <span class="sr-only">
            {{
              `${color.label}${isColorSelected(color.hex) ? " selected" : ""}`
            }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  reactive,
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  nextTick
} from "vue"
import { useI18n } from "vue-i18n"
import InputField from "./InputField.vue"

export default {
  name: "ColorPicker",
  components: { InputField },
  emits: ["update:model-value"],
  props: {
    inputLabel: String,
    invalidFeedback: [String, Array],
    modelValue: String
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const wrapperRef = ref(null)
    const colorButtonRefs = ref({})
    const activeColorHex = ref("")
    const inputId = `color-picker-${Math.random().toString(36).slice(2, 11)}`
    const inputLabelId = `${inputId}-label`

    const state = reactive({
      openModal: false
    })

    const colorPalette = [
      { label: "Color1", hex: "#B8D8F8" },
      { label: "Color2", hex: "#7986CB" },
      { label: "Color3", hex: "#FFC8B7" },
      { label: "Color4", hex: "#567593" },
      { label: "Color5", hex: "#33B679" },
      { label: "Color6", hex: "#F6C026" },
      { label: "Color7", hex: "#039BE5" },
      { label: "Color8", hex: "#A19AF5" },
      { label: "Color9", hex: "#E67C73" },
      { label: "Color10", hex: "#616161" },
      { label: "Color12", hex: "#008566" },
      { label: "Color13", hex: "#F5511D" },
      { label: "Color14", hex: "#3F51B5" },
      { label: "Color15", hex: "#8E24AA" },
      { label: "Color16", hex: "#D60000" }
    ]

    const selectedColorHex = computed(() => props.modelValue || "#fff")
    const triggerAriaLabel = computed(() => {
      const currentColor = selectedColorHex.value || t("common.none")
      const label = props.inputLabel || t("common.color")
      return t("common.a11y_color_picker_trigger_label", {
        label,
        currentColor
      })
    })
    const triggerInputAriaLabel = computed(() =>
      props.inputLabel ? "" : triggerAriaLabel.value
    )
    const radiogroupAriaLabel = computed(() => {
      const label = props.inputLabel || t("common.color")
      return label || ""
    })

    const getDefaultFocusableColorHex = () => {
      if (
        selectedColorHex.value &&
        colorPalette.some((item) => item?.hex === selectedColorHex.value)
      ) {
        return selectedColorHex.value
      }
      return colorPalette?.[0]?.hex || ""
    }

    const setInitialActiveColor = () => {
      activeColorHex.value = getDefaultFocusableColorHex()
    }

    const focusActiveColor = async () => {
      await nextTick()
      const activeButton = colorButtonRefs.value?.[activeColorHex.value]
      activeButton?.focus?.()
    }

    const focusTriggerInput = () => {
      const root = wrapperRef.value
      if (!root) return
      const input = root.querySelector(
        ".color-palette input, .color-palette textarea"
      )
      input?.focus?.()
    }

    const openModal = async () => {
      state.openModal = true
      setInitialActiveColor()
      await focusActiveColor()
    }

    const toggleModal = async () => {
      if (state.openModal) {
        closeModal()
        return
      }
      await openModal()
    }

    const closeModal = () => {
      state.openModal = false
      const targetId = inputId
      if (!targetId) return
      const triggerElement = document.getElementById(targetId)
      triggerElement?.focus?.()
    }

    const onPickerFocusOut = (event) => {
      const wrapper = wrapperRef.value
      if (!wrapper) return
      const next = event.relatedTarget
      if (next && wrapper.contains(next)) return
      nextTick(() => {
        if (!state.openModal) return
        const active = document.activeElement
        if (active && wrapper.contains(active)) return
        state.openModal = false
      })
    }

    const onEscape = () => {
      if (!state.openModal) return
      state.openModal = false
      nextTick(() => focusTriggerInput())
    }

    const isColorSelected = (hex) => selectedColorHex.value === hex

    const getColorAriaLabel = (color) => {
      const hex = color?.hex || ""
      const selected = hex ? isColorSelected(hex) : false

      if (!hex) {
        return (
          (typeof t === "function" && t?.("colorPicker.selectColorFallback")) ||
          "Select color"
        )
      }

      const translationKey = selected
        ? "colorPicker.selectColorSelected"
        : "colorPicker.selectColor"

      const translated =
        typeof t === "function" ? t?.(translationKey, { color: hex }) || "" : ""

      if (translated) {
        return translated
      }

      const selectedSuffix = selected ? ", selected" : ""
      return `Select ${hex}${selectedSuffix}`
    }

    const setColor = (color) => {
      if (!color) return
      activeColorHex.value = color
      emit("update:model-value", color)
      closeModal()
    }

    const selectColorWithoutClosing = (color) => {
      if (!color) return
      activeColorHex.value = color
    }

    const getRadioTabindex = (hex) => {
      if (!hex || !state.openModal) return -1
      return activeColorHex.value === hex ? 0 : -1
    }

    const setColorButtonRef = (element, hex) => {
      if (!hex) return
      if (!element) {
        delete colorButtonRefs.value[hex]
        return
      }
      colorButtonRefs.value[hex] = element
    }

    const getColorIndexByHex = (hex) =>
      colorPalette.findIndex((color) => color?.hex === hex)

    const getColorHexByIndex = (index) => colorPalette?.[index]?.hex || ""

    const focusAndSelectColorByIndex = async (index) => {
      const totalColors = colorPalette.length
      if (!totalColors) return

      const nextIndex = ((index % totalColors) + totalColors) % totalColors
      const nextColorHex = getColorHexByIndex(nextIndex)
      if (!nextColorHex) return

      selectColorWithoutClosing(nextColorHex)
      await focusActiveColor()
    }

    const onColorRadioKeydown = async (event, currentColorHex) => {
      const currentIndex = getColorIndexByHex(currentColorHex)
      if (currentIndex < 0) return

      switch (event?.key) {
        case "ArrowRight":
        case "ArrowDown":
          event?.preventDefault?.()
          await focusAndSelectColorByIndex(currentIndex + 1)
          break
        case "ArrowLeft":
        case "ArrowUp":
          event?.preventDefault?.()
          await focusAndSelectColorByIndex(currentIndex - 1)
          break
        case "Home":
          event?.preventDefault?.()
          await focusAndSelectColorByIndex(0)
          break
        case "End":
          event?.preventDefault?.()
          await focusAndSelectColorByIndex(colorPalette.length - 1)
          break
        case " ":
        case "Enter":
          event?.preventDefault?.()
          setColor(currentColorHex)
          break
        case "Escape":
          event?.preventDefault?.()
          closeModal()
          break
        default:
          break
      }
    }

    const onDocumentClick = (event) => {
      if (!state.openModal) return
      if (!wrapperRef.value?.contains(event.target)) {
        closeModal()
      }
    }

    onMounted(() => {
      document.addEventListener("click", onDocumentClick)
    })

    onUnmounted(() => {
      document.removeEventListener("click", onDocumentClick)
    })

    watch(
      () => props.modelValue,
      () => {
        if (!state.openModal) return
        setInitialActiveColor()
      }
    )

    return {
      wrapperRef,
      inputId,
      inputLabelId,
      state,
      colorPalette,
      setColorButtonRef,
      toggleModal,
      closeModal,
      onPickerFocusOut,
      onEscape,
      triggerInputAriaLabel,
      radiogroupAriaLabel,
      isColorSelected,
      getColorAriaLabel,
      getRadioTabindex,
      onColorRadioKeydown,
      setColor,
      selectedColorHex
    }
  }
}
</script>
